import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ClearIcon from '@mui/icons-material/Clear';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Button from '@mui/material/Button';
import Grid from '@mui/material/GridLegacy';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import Typography, { TypographyProps } from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { PostGreSQLInterval } from 'api';

interface PropTypes {
  id: string;
  start: string;
  end: string;
  courseTitle?: string;
  sectionTitle?: string;
  attendance: 'present' | 'absent' | 'no_permit' | null;
  instructors: string[];
  location: string | null;
  canceledAt: string | null;
  allowCancellation: boolean;
  lateCancellationDeadline?: PostGreSQLInterval | null;
  showDate: boolean;
  onClickCancel: (id: string) => void;
}

const IconList = styled(List)<{ component?: React.ElementType }>({
  padding: 0,
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: 4,
    color: 'inherit',
  },
  '& .MuiListItem-root': {
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 8,
  },
});

function StudentPeriod(props: PropTypes): JSX.Element {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'), { defaultMatches: true });
  dayjs.extend(advancedFormat);
  dayjs.extend(duration);
  dayjs.extend(relativeTime);

  // returns the typography component indicating the number of performance seats remaining in the period
  const getAttendanceText = (attendance: 'present' | 'absent' | 'no_permit' | null) => {
    // props to pass to all typography components
    const baseTextProps: TypographyProps = {
      variant: 'subtitle1',
      lineHeight: 1.25,
      fontWeight: 'bold',
      align: isXs ? 'center' : 'right',
    };

    if (!attendance) {
      return null;
    }

    if (attendance === 'present') {
      return (
        <Typography {...baseTextProps} color={theme.palette.success.main}>
          Present
        </Typography>
      );
    }

    if (attendance === 'no_permit') {
      return (
        <Typography {...baseTextProps} color={theme.palette.warning.main}>
          No Permit
        </Typography>
      );
    }

    if (attendance === 'absent') {
      return (
        <Typography {...baseTextProps} color={theme.palette.error.main}>
          Absent
        </Typography>
      );
    }
  };

  return (
    <Paper
      sx={{
        p: 1,
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#1A2027' : '#fff'),
        border: '1px solid black',
      }}
    >
      <Grid container direction="row">
        <Grid item xs={12} sm={6} container direction="column">
          <Grid item pb={1}>
            <Typography
              variant="subtitle1"
              lineHeight={1.25}
              fontWeight="bold"
              sx={{
                textDecoration: props?.canceledAt ? 'line-through' : 'unset',
                textDecorationThickness: 2,
              }}
            >
              {props.showDate && dayjs(props.start).format('dddd, MMMM Do')}
              {props.showDate && <br />}
              {`${dayjs(props.start).format('h:mm A')} - ${dayjs(props.end).format('h:mm A')}`}
            </Typography>
          </Grid>

          {props?.canceledAt && (
            <Grid item pb={1}>
              <Typography
                variant="subtitle1"
                lineHeight={1.25}
                fontWeight="bold"
                color="error.main"
                align={isXs ? 'center' : 'left'}
              >
                {`Canceled on ${dayjs(props.canceledAt).format('MMMM Do')} at ${dayjs(props.canceledAt).format('h:mm A')}`}
              </Typography>
            </Grid>
          )}

          <Grid item>
            <IconList>
              <ListItem>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText secondary={props?.instructors?.join(', ') ?? 'TBD'} />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <LocationOnIcon />
                </ListItemIcon>
                <ListItemText secondary={props?.location ?? 'TBD'} />
              </ListItem>
            </IconList>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          container
          direction="column"
          justifyContent="space-between"
          alignItems={{
            xs: undefined,
            sm: 'flex-end',
          }}
          flexWrap={'nowrap'}
        >
          <Grid item py={isXs ? 1 : 0}>
            <Typography variant="subtitle1" lineHeight={1.25} fontWeight="bold" align={isXs ? 'center' : 'right'}>
              {props?.courseTitle}
            </Typography>
            <Typography variant="subtitle2" lineHeight={1.25} fontWeight="bold" align={isXs ? 'center' : 'right'}>
              {props?.sectionTitle}
            </Typography>
            {getAttendanceText(props?.attendance)}
          </Grid>
          <Grid item>
            {!props?.canceledAt &&
              props?.allowCancellation &&
              props?.attendance === null &&
              new Date(props?.start) >= new Date() && (
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  endIcon={<ClearIcon />}
                  sx={{
                    py: 0,
                    mt: 1,
                  }}
                  onClick={() => props?.onClickCancel(props?.id)}
                >
                  Cancel
                </Button>
              )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default StudentPeriod;
